package us.laantech.common.util

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.offsetAt
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

fun Long.tryFormatAsDateTime(): String? = tryFormatAsDateTime(this)

/**
 * Render a Long as `YYYY-MM-DD HH:MM` (UTC + local) if it looks like a plausible
 * epoch-millis value (between year ~2001 and year ~2100). Otherwise null.
 */
fun tryFormatAsDateTime(epochMillis: Long, showUTC: Boolean = false): String? {
    // Roughly: 2001-01-01 .. 2100-01-01 in UTC ms.
    val lower = 1_000_000_000_000L
    val upper = 4_102_444_800_000L
    if (epochMillis !in lower..upper) return null
    return runCatching {
        val instant = Instant.fromEpochMilliseconds(epochMillis)
        val utc = instant.toLocalDateTime(TimeZone.UTC)
        val localZone = TimeZone.currentSystemDefault()
        val local = instant.toLocalDateTime(localZone)
        val offset = localZone.offsetAt(instant).toString() // e.g. "-05:00" or "Z"

        if (showUTC) return@runCatching "${formatDateTime24h(utc)} UTC"
        if (!showUTC) return@runCatching formatDateTime12h(local)

        "≈ ${formatDateTime(utc)} UTC  •  ${formatDateTime(local)} ($offset) ${localZone.id}"
    }.getOrNull()
}

private fun formatDateTime(ldt: LocalDateTime): String {
    val mm = ldt.monthNumber.toString().padStart(2, '0')
    val dd = ldt.dayOfMonth.toString().padStart(2, '0')
    val hh = ldt.hour.toString().padStart(2, '0')
    val mi = ldt.minute.toString().padStart(2, '0')
    return "${ldt.year}-$mm-$dd $hh:$mi"
}

private fun formatDateTime12h(ldt: LocalDateTime): String {
    val mm = ldt.monthNumber.toString().padStart(2, '0')
    val dd = ldt.dayOfMonth.toString().padStart(2, '0')
    val hour24 = ldt.hour
    val amPm = if (hour24 < 12) "AM" else "PM"
    val hour12 = when {
        hour24 == 0 -> 12
        hour24 > 12 -> hour24 - 12
        else -> hour24
    }
    val hh = hour12.toString().padStart(2, '0')
    val mi = ldt.minute.toString().padStart(2, '0')
    return "${ldt.year}-$mm-$dd $hh:$mi $amPm"
}

private fun formatDateTime24h(ldt: LocalDateTime): String {
    val mm = ldt.monthNumber.toString().padStart(2, '0')
    val dd = ldt.dayOfMonth.toString().padStart(2, '0')
    val hh = ldt.hour.toString().padStart(2, '0')
    val mi = ldt.minute.toString().padStart(2, '0')
    return "${ldt.year}-$mm-$dd $hh:$mi"
}